import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useRecommendedPlanComparison } from 'customHooks/useRecommendedPlanComparison';

const usePlanOverageUsageData = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();

    if (!validGivebutterPlusPlan) {
        return {
            over80: null,
            over90: null,
            over100: null,
            hasOverages: null,
            isGracePeriod: null,
        };
    }
    const { tier, is_reporting_contact_overage } = validGivebutterPlusPlan;

    const percentageUsed = tier.current_contacts_count / tier.max_contacts;
    const over80 = percentageUsed >= 0.8;
    const over90 = percentageUsed >= 0.9;
    const over100 = percentageUsed > 1;

    const isGracePeriod = !is_reporting_contact_overage;

    const hasOverages =
        (!isGracePeriod && validGivebutterPlusPlan.tier.overage_pack_count > 0) ||
        (isGracePeriod && validGivebutterPlusPlan.tier.unreported_overage_pack_count > 0);

    return {
        over80,
        over90,
        over100,
        hasOverages,
        isGracePeriod,
    };
};

const useOveragesShouldWarnBannerChecks = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { isRecommendedPlanCheaper } = useRecommendedPlanComparison();
    const { over80, over90, over100, isGracePeriod } = usePlanOverageUsageData();

    const {
        hasDismissed80WarningBanner,
        hasDismissed90WarningBanner,
        hasDismissed100WarningBanner,
        hasDismissedOveragesWarningGracePeriodBanner,
        hasDismissedOveragesWarningBanner,
    } = useAccountLocalStorage(
        ({
            hasDismissed80WarningBanner,
            hasDismissed90WarningBanner,
            hasDismissed100WarningBanner,
            hasDismissedOveragesWarningGracePeriodBanner,
            hasDismissedOveragesWarningBanner,
        }) => {
            return {
                hasDismissed80WarningBanner,
                hasDismissed90WarningBanner,
                hasDismissed100WarningBanner,
                hasDismissedOveragesWarningGracePeriodBanner,
                hasDismissedOveragesWarningBanner,
            };
        },
    );

    if (!validGivebutterPlusPlan) {
        return {
            shouldPromptToManagePlans: false,
            shouldWarn80Used: false,
            shouldWarn90Used: false,
            shouldWarn100Used: false,
        };
    }

    const { tier } = validGivebutterPlusPlan;

    const shouldWarnGracePeriodOverages =
        isGracePeriod &&
        tier.unreported_overage_pack_count > 0 &&
        !hasDismissedOveragesWarningGracePeriodBanner;

    const shouldWarnStandardOverages =
        !isGracePeriod && tier.overage_pack_count > 0 && !hasDismissedOveragesWarningBanner;

    const shouldPromptToManagePlans =
        isRecommendedPlanCheaper && (shouldWarnGracePeriodOverages || shouldWarnStandardOverages);

    const shouldWarn80Used = over80 && !over90 && !hasDismissed80WarningBanner;
    const shouldWarn90Used = over90 && !over100 && !hasDismissed90WarningBanner;
    const shouldWarn100Used = over100 && !hasDismissed100WarningBanner;

    return {
        shouldPromptToManagePlans,
        shouldWarn80Used,
        shouldWarn90Used,
        shouldWarn100Used,
    };
};

const useOveragesShouldWarnBillingChecks = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { isRecommendedPlanCheaper } = useRecommendedPlanComparison();
    const { isGracePeriod } = usePlanOverageUsageData();

    const { hasDismissedOveragesWarningGracePeriodBilling, hasDismissedOveragesWarningBilling } =
        useAccountLocalStorage(
            ({
                hasDismissedOveragesWarningGracePeriodBilling,
                hasDismissedOveragesWarningBilling,
            }) => {
                return {
                    hasDismissedOveragesWarningGracePeriodBilling,
                    hasDismissedOveragesWarningBilling,
                };
            },
        );

    if (!validGivebutterPlusPlan) {
        return {
            shouldPromptToManagePlans: false,
        };
    }

    const { tier } = validGivebutterPlusPlan;

    const shouldWarnStandardOverages =
        !isGracePeriod && tier.overage_pack_count > 0 && !hasDismissedOveragesWarningBilling;

    const shouldWarnGracePeriodOverages =
        isGracePeriod &&
        tier.unreported_overage_pack_count > 0 &&
        !hasDismissedOveragesWarningGracePeriodBilling;

    const shouldPromptToManagePlans =
        isRecommendedPlanCheaper && (shouldWarnGracePeriodOverages || shouldWarnStandardOverages);

    return {
        shouldPromptToManagePlans,
    };
};

export {
    usePlanOverageUsageData,
    useOveragesShouldWarnBannerChecks,
    useOveragesShouldWarnBillingChecks,
};
